import React, { useState, useEffect } from "react";
import { FAVORITOS_UPDATED_EVENT } from "../utils/favoritos";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "/api";
const FALLBACK_RESOURCE_IMAGE = "/placeholder.png";

function isDataImageUrl(value) {
  return typeof value === "string" && value.startsWith("data:image/");
}

function getAuthValue(key) {
  const ls = localStorage.getItem(key);
  if (ls !== null && ls !== "undefined") return ls;
  const ss = sessionStorage.getItem(key);
  return ss !== "undefined" ? ss : null;
}

function buildRecursoGoUrl(apiBase, recursoId) {
  const base = String(apiBase || "/api").replace(/\/$/, "");
  const href = `${base}/recursos/${recursoId}/go`;
  const token = getAuthValue("token");
  if (!token) return href;
  return `${href}?token=${encodeURIComponent(token)}`;
}

const Sidebar = ({ isOpen, offsetTop = 75 }) => {
  const [recientes, setRecientes] = useState([]);
  const [favoritos, setFavoritos] = useState([]);
  const [imagenes, setImagenes] = useState({});
  const [mostrarRecientes, setMostrarRecientes] = useState(true);
  const [mostrarFavoritos, setMostrarFavoritos] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [resultadosBusqueda, setResultadosBusqueda] = useState([]);
  const [timeoutBusqueda, setTimeoutBusqueda] = useState(null);

  const [todosRecursos, setTodosRecursos] = useState([]);
  const [modoBusqueda, setModoBusqueda] = useState("frontend");

  const [, setPermisos] = useState([]);
  const token = localStorage.getItem("token");
  const rol = localStorage.getItem("rol");
  const userId = localStorage.getItem("userId");

  useEffect(() => {
    const getStorageForFavoritos = () => {
      const esInvitado = getAuthValue("guest") === "true";
      const esAnonimo = getAuthValue("anonimo") === "true";
      const esAutenticado = !esInvitado && !esAnonimo && !!getAuthValue("token");
      return esAutenticado ? localStorage : sessionStorage;
    };

    const cargarColeccionesSidebar = () => {
      const storage = getStorageForFavoritos();
      const recientesData = JSON.parse(localStorage.getItem("recientes") || "[]");
      const favoritosDataRaw = JSON.parse(storage.getItem("favoritos") || "[]");

      // Filtrar recursos INACTIVO para usuarios no-admin (mejor UX)
      const esAdmin = rol === "LIBRARIAN" || rol === "ADMIN";
      const favoritosData = esAdmin
        ? favoritosDataRaw
        : (Array.isArray(favoritosDataRaw) ? favoritosDataRaw.filter((r) => r.estado !== "INACTIVO") : []);

      const recientesFiltered = Array.isArray(recientesData)
        ? (esAdmin ? recientesData : recientesData.filter((r) => r.estado !== "INACTIVO"))
        : [];

      setRecientes(recientesFiltered.slice(0, 3));
      setFavoritos(favoritosData);

      return { recientesData: recientesFiltered, favoritosData };
    };

    const fetchImagenRecurso = async (recursoId) => {
      try {
        const response = await fetch(`${API_BASE}/recursos/${recursoId}/imagen`);
        if (!response.ok) throw new Error();
        const txt = await response.text();
        return isDataImageUrl(txt) ? txt : FALLBACK_RESOURCE_IMAGE;
      } catch {
        return FALLBACK_RESOURCE_IMAGE;
      }
    };

    const cargarImagenesLista = async (lista) => {
      const idsUnicos = [
        ...new Set((Array.isArray(lista) ? lista : []).map((r) => r?.id).filter(Boolean)),
      ];

      if (idsUnicos.length === 0) return;

      const pares = await Promise.all(
        idsUnicos.map(async (id) => [id, await fetchImagenRecurso(id)]),
      );

      setImagenes((prev) => {
        const next = { ...prev };
        for (const [id, src] of pares) {
          next[id] = src;
        }
        return next;
      });
    };

    const { recientesData, favoritosData } = cargarColeccionesSidebar();

    const cargarRecursos = async () => {
      try {
        const res = await fetch(`${API_BASE}/recursos`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) throw new Error();

        const data = await res.json();
        const activos = Array.isArray(data)
          ? data.filter((recurso) => recurso.estado !== "INACTIVO")
          : [];

        if (activos.length <= 1000) {
          setModoBusqueda("frontend");
          setTodosRecursos(activos);
        } else {
          setModoBusqueda("backend");
        }
      } catch (error) {
        console.error("Error cargando recursos:", error);
      }
    };

    cargarRecursos();
    cargarImagenesLista([...recientesData, ...favoritosData]);

    const refreshColecciones = () => {
      const { recientesData: recData, favoritosData: favData } = cargarColeccionesSidebar();
      cargarImagenesLista([...recData, ...favData]);
    };

    window.addEventListener("storage", refreshColecciones);
    window.addEventListener(FAVORITOS_UPDATED_EVENT, refreshColecciones);
    window.addEventListener("auth_changed", refreshColecciones);
    window.addEventListener("recientes-actualizados", refreshColecciones);

    const cargarPermisos = async () => {
      if (rol === "LIBRARIAN" && userId) {
        try {
          const res = await fetch(`${API_BASE}/permisos/${userId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) throw new Error();
          const data = await res.json();
          setPermisos(data);
        } catch (err) {
          console.error("Error al obtener permisos:", err);
        }
      }
    };

    cargarPermisos();

    return () => {
      window.removeEventListener("storage", refreshColecciones);
      window.removeEventListener(FAVORITOS_UPDATED_EVENT, refreshColecciones);
      window.removeEventListener("auth_changed", refreshColecciones);
      window.removeEventListener("recientes-actualizados", refreshColecciones);
    };
  }, [rol, token, userId]);

  const normalizarTexto = (texto) => {
    return texto
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  };

  const cargarImagenRecurso = async (recursoId) => {
    try {
      const response = await fetch(`${API_BASE}/recursos/${recursoId}/imagen`);
      if (!response.ok) throw new Error();
      const txt = await response.text();
      return isDataImageUrl(txt) ? txt : FALLBACK_RESOURCE_IMAGE;
    } catch {
      return FALLBACK_RESOURCE_IMAGE;
    }
  };

  const buscarRecursosPorNombre = async (texto) => {
    if (!texto.trim()) {
      setResultadosBusqueda([]);
      return;
    }

    if (modoBusqueda === "frontend") {
      const textoNormalizado = normalizarTexto(texto);
      const palabrasBusqueda = textoNormalizado.split(" ");

      const filtrados = todosRecursos
        .filter((recurso) => recurso.estado !== "INACTIVO")
        .filter((recurso) => {
          const nombreNormalizado = normalizarTexto(recurso.nombre);

          return palabrasBusqueda.every((palabra) =>
            nombreNormalizado.includes(palabra),
          );
        });

      setResultadosBusqueda(filtrados);
    } else {
      try {
        const res = await fetch(
          `${API_BASE}/recursos/buscar?q=${encodeURIComponent(texto)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (!res.ok) throw new Error();

        const data = await res.json();
        setResultadosBusqueda(data.filter((recurso) => recurso.estado !== "INACTIVO"));
      } catch (error) {
        console.error("Error buscando en backend:", error);
      }
    }
  };

  useEffect(() => {
    if (!Array.isArray(resultadosBusqueda) || resultadosBusqueda.length === 0) {
      return;
    }

    const faltantes = resultadosBusqueda.filter((recurso) => !imagenes[recurso.id]);
    if (faltantes.length === 0) return;

    let cancelado = false;

    const cargarImagenesResultados = async () => {
      const pares = await Promise.all(
        faltantes.map(async (recurso) => [
          recurso.id,
          await cargarImagenRecurso(recurso.id),
        ]),
      );

      if (cancelado) return;

      setImagenes((prev) => {
        const siguientes = { ...prev };
        for (const [id, src] of pares) {
          siguientes[id] = src;
        }
        return siguientes;
      });
    };

    cargarImagenesResultados();

    return () => {
      cancelado = true;
    };
  }, [resultadosBusqueda, imagenes]);

  return (
    <aside
      className={`fixed left-0 bg-white shadow-md z-40 w-64 transition-transform duration-500 ease-in-out ${isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      style={{ top: offsetTop, height: `calc(100vh - ${offsetTop}px)` }}
    >
      <div className="p-4 overflow-y-auto h-full text-sm">
        <div className="mb-6">
          <label className="block font-semibold mb-1">Filtro</label>
          <input
            type="text"
            placeholder="Filtrar bases, libros y más"
            className="w-full p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring focus:border-blue-300"
            value={busqueda}
            onChange={(e) => {
              const valor = e.target.value;
              setBusqueda(valor);

              if (timeoutBusqueda) clearTimeout(timeoutBusqueda);

              const nuevoTimeout = setTimeout(() => {
                buscarRecursosPorNombre(valor);
              }, 300);

              setTimeoutBusqueda(nuevoTimeout);
            }}
          />
          {busqueda && (
            <div className="mt-4">
              <h3 className="text-sm font-semibold mb-2">Resultados:</h3>
              <div className="grid grid-cols-2 gap-2">
                {resultadosBusqueda.length > 0 ? (
                  resultadosBusqueda.map((recurso) => (
                    <div
                      key={recurso.id}
                      className="flex flex-col items-center w-full"
                    >
                      <a
                        href={buildRecursoGoUrl(API_BASE, recurso.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={imagenes[recurso.id] || FALLBACK_RESOURCE_IMAGE}
                          alt={recurso.nombre}
                          className="w-24 h-16 object-cover rounded"
                          onError={(e) => {
                            e.currentTarget.src = FALLBACK_RESOURCE_IMAGE;
                          }}
                        />
                      </a>
                      <p className="text-xs text-center mt-1 truncate w-full">
                        {recurso.nombre}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-gray-500 col-span-2 text-center">
                    No se encontraron resultados.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mb-4">
          <div
            className="flex justify-between items-center cursor-pointer px-1 py-2 font-semibold border-b"
            onClick={() => setMostrarRecientes(!mostrarRecientes)}
          >
            <span>Recientes</span>
            <span>{mostrarRecientes ? "v" : ">"}</span>
          </div>
          <div
            className={`grid grid-cols-2 gap-2 transition-all duration-300 overflow-hidden ${mostrarRecientes ? "max-h-96 pt-2" : "max-h-0"
              }`}
          >
            {recientes.map((recurso) => (
              <div
                key={recurso.id}
                className="flex flex-col items-center w-full"
              >
                <a
                  href={buildRecursoGoUrl(API_BASE, recurso.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={imagenes[recurso.id] || FALLBACK_RESOURCE_IMAGE}
                    alt={recurso.nombre}
                    className="w-24 h-16 object-cover rounded"
                    onError={(e) => {
                      e.currentTarget.src = FALLBACK_RESOURCE_IMAGE;
                    }}
                  />
                </a>
                <p className="text-xs text-center mt-1 truncate w-full">
                  {recurso.nombre}
                </p>
              </div>
            ))}
            {recientes.length === 0 && (
              <p className="text-xs text-gray-500 col-span-2 text-center">
                No hay elementos recientes.
              </p>
            )}
          </div>
        </div>

        <div className="mb-4">
          <div
            className="flex justify-between items-center cursor-pointer px-1 py-2 font-semibold border-b"
            onClick={() => setMostrarFavoritos(!mostrarFavoritos)}
          >
            <span>Favoritos</span>
            <span>{mostrarFavoritos ? "v" : ">"}</span>
          </div>
          <div
            className={`grid grid-cols-2 gap-2 transition-all duration-300 overflow-hidden ${mostrarFavoritos ? "max-h-96 pt-2" : "max-h-0"
              }`}
          >
            {favoritos.map((recurso) => (
              <div
                key={recurso.id}
                className="flex flex-col items-center w-full"
              >
                <a
                  href={buildRecursoGoUrl(API_BASE, recurso.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={imagenes[recurso.id] || FALLBACK_RESOURCE_IMAGE}
                    alt={recurso.nombre}
                    className="w-24 h-16 object-cover rounded"
                    onError={(e) => {
                      e.currentTarget.src = FALLBACK_RESOURCE_IMAGE;
                    }}
                  />
                </a>
                <p className="text-xs text-center mt-1 truncate w-full">
                  {recurso.nombre}
                </p>
              </div>
            ))}
            {favoritos.length === 0 && (
              <p className="text-xs text-gray-500 col-span-2 text-center">
                No hay elementos favoritos.
              </p>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;

